/**
 * VAL-DENTAL-RISK-GUIDE-1 — dental guidance beside "Company-specific risk".
 * Pure, no React. Text and sources are the owner-approved research
 * (2026-10-01). No per-sign points: none are published. Never blocks.
 */
export interface CsrpLine { text: string; url: string }
export interface CsrpGuide {
  range: CsrpLine
  signs: CsrpLine[]
  countOnce: CsrpLine
  evidence: CsrpLine
}

const GATP = 'https://gatpsolutions.com/blog/dental-practice-valuation-methods/'
const DDS = 'https://ddsmatch.com/articles/dental-practice-risk-assessment-valuation-buyers/'

const DENTAL_GUIDE: CsrpGuide = {
  range: {
    text: 'Typical company-specific risk for a small private business: 0% to 10%.',
    url: 'https://www.gma-cpa.com/blog/understanding-the-company-specific-risk-premium-a-guide-for-attorneys',
  },
  signs: [
    { text: 'One dentist does most of the production and there is no associate.', url: GATP },
    {
      text: 'Office lease has less than 5 years left (lenders usually want 5 to 10).',
      url: 'https://practiceorbit.com/blog/what-if-my-lease-only-has-a-few-years-left-can-i-still-sell-my-dental-practice',
    },
    { text: 'Heavy reliance on low-paying insurance plans.', url: DDS },
    { text: 'Outdated equipment, or collections falling year over year.', url: DDS },
    { text: 'Hygiene below 40% of production.', url: GATP },
  ],
  countOnce: {
    text: 'Count each risk once. If one-dentist risk is in this number, do not also take a separate key-person discount.',
    url: 'https://www.msgcpa.com/forensicperspectives/unlocking-key-person-risk-business-valuation/',
  },
  evidence: {
    text: 'Courts cut company-specific risk that is not tied to evidence; say which signs apply.',
    url: 'https://www.kroll.com/en/publications/expert-services/from-the-parlor-to-the-courtroom-the-use-of-company-specific-risk-premium-in-valuations',
  },
}

const WARNING_TEXT = 'Above the typical 0-10% range. Allowed; say which signs apply.'

const isDental = (industry: string | null | undefined): boolean =>
  (industry ?? '').trim().toLowerCase() === 'dental'

export function csrpGuidance(industry: string | null | undefined): CsrpGuide | null {
  return isDental(industry) ? DENTAL_GUIDE : null
}

export function csrpWarning(value: string, industry: string | null | undefined): string | null {
  if (!isDental(industry) || value.trim() === '') return null
  const n = Number(value)
  return Number.isFinite(n) && n > 10 ? WARNING_TEXT : null
}
